import React, { useState, useEffect, useRef } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  ArrowRight,
  Star,
  Zap,
  Heart,
  Users,
  Sun,
} from "lucide-react";

import ContactUsBlock from "../reusable-components/ContactUsBlock";
import ContactUsBlockMobile from "../reusable-components/ContactUsBlockMobile";
import contactusphoto from "../../src/Assets/bb_contactus.jpg";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    projectType: "web-development",
  });

  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const heroRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Handle form submission
  };

  const floatingElements = [
    { icon: Star, color: "text-blue-500", delay: "0s" },
    { icon: Zap, color: "text-indigo-500", delay: "2s" },
    { icon: Heart, color: "text-rose-500", delay: "4s" },
    { icon: Users, color: "text-emerald-500", delay: "6s" },
    { icon: Sun, color: "text-yellow-500", delay: "1s" },
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      subtitle: "Drop us a line anytime",
      content: "kripa@bytesberry.com",
      href: "mailto:kripa@bytesberry.com",
      color: "bg-blue-500",
    },
    {
      icon: Phone,
      title: "Call Us",
      subtitle: "Mon-Fri 9AM-5PM",
      content: "+91 78729 69664, +91 85973 55169",
      href: "tel:+9178729 69664",
      color: "bg-emerald-500",
    },
    {
      icon: MapPin,
      title: "Visit Us",
      subtitle: "Come say hello",
      content: "Basnett Building, D.P.H Road, Gangtok, Sikkim.",
      href: "#map",
      color: "bg-rose-500",
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden font-poppins">
      {/* Animated background elements - subtle */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full blur-3xl animate-pulse opacity-30"></div>
        <div
          className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-rose-50 to-pink-50 rounded-full blur-3xl animate-pulse opacity-30"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      {/* Floating elements - positioned over hero */}
      {/* {floatingElements.map((Element, index) => (
        <div
          key={index}
          className={`fixed ${Element.color} opacity-30 pointer-events-none hidden lg:block z-20`}
          style={{
            top: `${20 + index * 15}%`,
            left: `${10 + index * 20}%`,
            animation: `float 6s ease-in-out infinite`,
            animationDelay: Element.delay,
          }}
        >
          <Element.icon size={24} />
        </div>
      ))} */}

      {/* Hero Section with Background Image */}
      <section
        ref={heroRef}
        className={`relative flex items-center justify-center overflow-hidden transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0 h-full">
          <img
            src={contactusphoto}
            alt="Contact Us Background"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-24 ">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight pt-16">
            Get in Touch
          </h1>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-blue-400 to-indigo-400 mx-auto rounded-full mb-6"></div>
          <p className=" md:block text-center text-lg sm:text-xl lg:text-2xl font-normal text-white max-w-4xl mx-auto">
            Got a project in mind? We'd love to hear about it.
          </p>
        </div>
      </section>

      {/* Mobile subtitle
      <section className="block md:hidden bg-white py-8 px-4">
        <p className="text-center text-base font-medium text-gray-600 max-w-sm mx-auto">
          Got a project in mind? We'd love to hear about it.
        </p>
      </section> */}

      {/* Contact Information Cards */}
      <section
        id="contact-info"
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary mb-4">
              Connect with{" "}
              <span className="bg-gradient-to-r from-tertiary to-primary bg-clip-text text-transparent">
                Us
              </span>
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Choose the way that works best for you. We're always ready to
              chat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`group relative p-6 sm:p-8 rounded-2xl transition-all duration-500 cursor-pointer transform hover:scale-100 ${
                  hoveredCard === index
                    ? "bg-gray-50 shadow-2xl shadow-gray-500/20"
                    : "bg-white shadow-lg"
                } border border-gray-100 hover:border-gray-200 flex flex-col items-center text-center md:items-start md:text-left`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => {
                  if (info.href.startsWith("#")) {
                    document
                      .getElementById(info.href.slice(1))
                      .scrollIntoView({ behavior: "smooth" });
                  } else {
                    window.location.href = info.href;
                  }
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <div className="relative z-10 flex flex-col items-center md:items-start">
                  <div
                    className={`mb-4 inline-block p-3 ${info.color} rounded-xl shadow-lg`}
                  >
                    <info.icon className="w-6 h-6 text-white" />
                  </div>

                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {info.title}
                  </h4>
                  <p className="text-gray-500 text-sm mb-3">{info.subtitle}</p>
                  <p className="text-lg text-gray-700 group-hover:text-gray-900 transition-colors">
                    {info.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section
        id="contact-form"
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          {/* Use the original contact blocks */}
          <div className="hidden xl:block px-24">
            <ContactUsBlock />
          </div>
          <div className="block xl:hidden">
            <ContactUsBlockMobile />
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section
        id="map"
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Find Us on the{" "}
              <span className="bg-gradient-to-r from-tertiary to-primary bg-clip-text text-transparent">
                Map
              </span>
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 px-4">
              Located in the heart of Gangtok, Sikkim.
            </p>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d443.0434873413098!2d88.613123!3d27.33359!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xbb211924c5b2dccc!2sBytesberry%20Technology!5e0!3m2!1sen!2sin!4v1672312998215!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-64 sm:h-80 md:h-96 lg:h-112"
            />
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 text-center bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Turn Your Vision Into Reality?
          </h3>
          <p className="text-lg text-gray-600 mb-8 px-4">
            Let's discuss your project and create something amazing together.
          </p>
          <button
            onClick={() =>
              document
                .getElementById("contact-form")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-tertiary to-primary text-white rounded-full font-semibold text-base sm:text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 flex items-center justify-center gap-2 mx-auto"
          >
            Get Started Today
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
